import { useContext } from "react";
import { Link } from "react-router-dom";
import { Breadcrumb, Button, Col, Row, Tabs, Tag } from "antd";
import {
    ArrowLeftOutlined,
    AuditOutlined,
    BarcodeOutlined,
    UserOutlined,
} from "@ant-design/icons";
import { FaFileInvoice, FaReceipt, FaTicketAlt } from "react-icons/fa";

import useViewAppUserAuto from "./useViewAppUserAuto";
import PageHeader from "../../../components/PageHeader";
import Loading from "../../../components/Loading";
import KpiCard from "../../../components/KpiCard";
import TabTaxesAuto from "./components/Tabs/TabTaxesAuto";
import TabTicketsAuto from "./components/Tabs/TabTicketsAuto";
import ModalGiveawayTicketsAuto from "./components/ModalGiveawayTicketsAuto";
import { ThemeContext } from "../../../contexts/ThemeContext";
import { formatCpfCnpj } from "../../../utils/Masks";
import { poeValorComPontosEmMil } from "../../../utils/Conversores";

interface IProps {
    defaultTab?: "impostos" | "bilhetes";
}

export default function ViewAppUserAuto({ defaultTab }: IProps) {
    const { theme } = useContext<any>(ThemeContext);
    const isDark = theme === "dark";

    const {
        appUserAuto,
        isLoading,
        activeTab,
        handleTabChange,
        taxesTableData,
        taxesColumns,
        taxesPagination,
        setTaxesPagination,
        giveawaysTableData,
        giveawaysColumns,
        giveawaysPagination,
        setGiveawaysPagination,
        selectedGiveaway,
        showModalTickets,
        handleOpenModalTickets,
        handleCloseModalTickets,
        totalTicketsCount,
        participatedGiveawaysCount,
        navigate,
    } = useViewAppUserAuto({ defaultTab });

    const tabItems = [
        {
            key: "impostos",
            label: (
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <AuditOutlined style={{ color: "#fa8c16" }} />
                    <span>Impostos ({taxesTableData.length})</span>
                </span>
            ),
            children: (
                <TabTaxesAuto
                    tableData={taxesTableData}
                    columns={taxesColumns}
                    isLoading={isLoading}
                    pagination={taxesPagination}
                    setPagination={setTaxesPagination}
                />
            ),
        },
        {
            key: "bilhetes",
            label: (
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <BarcodeOutlined style={{ color: "#1677ff" }} />
                    <span>Bilhetes por Sorteio ({giveawaysTableData.length})</span>
                </span>
            ),
            children: (
                <TabTicketsAuto
                    tableData={giveawaysTableData}
                    columns={giveawaysColumns}
                    isLoading={isLoading}
                    pagination={giveawaysPagination}
                    setPagination={setGiveawaysPagination}
                    onSelectGiveaway={handleOpenModalTickets}
                />
            ),
        },
    ];

    return (
        <Loading loading={isLoading}>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {/* External Breadcrumb Card */}
                <div
                    style={{
                        background: isDark ? "#141414" : "#ffffff",
                        borderRadius: 10,
                        border: `1px solid ${isDark ? "#262626" : "#eaedf1"}`,
                        boxShadow: isDark
                            ? "0 4px 20px rgba(0, 0, 0, 0.35)"
                            : "0 2px 10px rgba(0, 0, 0, 0.03)",
                        padding: "10px 16px",
                        display: "flex",
                        alignItems: "center",
                    }}
                >
                    <Breadcrumb
                        items={[
                            {
                                title: <Link to="/usuarios/auto">Usuários (IPTU)</Link>,
                            },
                            {
                                title: appUserAuto?.name || "Visualizar Usuário",
                            },
                        ]}
                    />
                </div>

                <PageHeader
                    icon={<UserOutlined />}
                    title={appUserAuto?.name || "Visualizar Usuário IPTU"}
                    tag={
                        appUserAuto?.cpfCnpj ? (
                            <Tag color="blue" style={{ borderRadius: 6, fontWeight: 600 }}>
                                {formatCpfCnpj(appUserAuto.cpfCnpj)}
                            </Tag>
                        ) : undefined
                    }
                    subtitle={
                        appUserAuto
                            ? `Contribuinte IPTU/ITBI • ${appUserAuto.cpfCnpj && appUserAuto.cpfCnpj.length > 11 ? "Pessoa Jurídica (CNPJ)" : "Pessoa Física (CPF)"}`
                            : "Detalhes do contribuinte, histórico de tributos e bilhetes de sorteios"
                    }
                    actions={
                        <Button
                            icon={<ArrowLeftOutlined />}
                            onClick={() => navigate("/usuarios/auto")}
                        >
                            Voltar
                        </Button>
                    }
                />

                {/* KPI Summary Cards */}
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={8}>
                        <KpiCard
                            title="Total de Bilhetes"
                            value={poeValorComPontosEmMil(totalTicketsCount)}
                            icon={<FaReceipt />}
                            accentColor="#1677ff"
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <KpiCard
                            title="Total de Impostos"
                            value={taxesTableData.length}
                            icon={<FaFileInvoice />}
                            accentColor="#fa8c16"
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <KpiCard
                            title="Sorteios com Bilhetes"
                            value={participatedGiveawaysCount}
                            icon={<FaTicketAlt />}
                            accentColor="#722ed1"
                        />
                    </Col>
                </Row>

                {/* Tabs Container */}
                <div
                    style={{
                        background: isDark ? "#141414" : "#ffffff",
                        borderRadius: 14,
                        border: `1px solid ${isDark ? "#262626" : "#eaedf1"}`,
                        boxShadow: isDark
                            ? "0 4px 20px rgba(0, 0, 0, 0.35)"
                            : "0 2px 10px rgba(0, 0, 0, 0.03)",
                        padding: "16px 20px",
                    }}
                >
                    <Tabs
                        activeKey={activeTab}
                        onChange={handleTabChange}
                        items={tabItems}
                    />
                </div>

                {/* Modal of giveaway tickets */}
                <ModalGiveawayTicketsAuto
                    open={showModalTickets}
                    onClose={handleCloseModalTickets}
                    giveaway={selectedGiveaway}
                    appUserAuto={appUserAuto}
                />
            </div>
        </Loading>
    );
}
